import type {
  FunctionComponent,
  HTMLProps,
  MouseEventHandler,
  PointerEventHandler,
  TouchEventHandler,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import { ChevronLeftIcon } from '../Icons/Navigational/ChevronLeft'
import { ChevronRightIcon } from '../Icons/Navigational/ChevronRight'
import styles from './TraverseButton.module.scss'

export const TraverseButton: FunctionComponent<Props> = ({
  direction,
  disabled = false,
  labelSuffix = '',
  className,
  onPointerDown,
  onPointerUp,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  ...rest
}) => {
  const isPrevious = direction === DIRECTION.PREVIOUS
  const eventHandlerProps = !disabled
    ? {
        onPointerDown,
        onPointerUp,
        onClick,
        onMouseDown,
        onMouseUp,
        onMouseLeave,
        onTouchStart,
        onTouchEnd,
      }
    : {}

  const Icon = isPrevious ? ChevronLeftIcon : ChevronRightIcon

  return (
    <div
      data-testid="button-traverse"
      className={classnames(
        styles.container,
        isPrevious ? styles.previousContainer : styles.nextContainer,
        className
      )}
      {...rest}
    >
      <button
        type="button"
        aria-label={`${isPrevious ? 'Previous' : 'Next'} ${labelSuffix}`.trim()}
        className={classnames(
          styles.button,
          isPrevious ? styles.previousButton : styles.nextButton
        )}
        disabled={disabled}
        {...eventHandlerProps}
      >
        <Icon className={styles.icon} customColour="white" />
      </button>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export const DIRECTION = {
  PREVIOUS: 'previous',
  NEXT: 'next',
}

type Props = {
  /**
   * Direction in which the button is intended to navigate the user on click.
   */
  direction: 'previous' | 'next'
  /**
   * Determines whether the button should be disabled, in which case it will be hidden.
   */
  disabled?: boolean
  /**
   * Parameter which allows you to make the label text more descriptive, such as "images" would
   * result in the label being "Next/Previous images" rather than just "Next/Previous".
   */
  labelSuffix?: string
  /**
   * Class name to attach to the main container.
   */
  className?: string
  /**
   * Pointer down event handler to attach to the button.
   */
  onPointerDown?: PointerEventHandler<HTMLButtonElement>
  /**
   * Pointer up event handler to attach to the button.
   */
  onPointerUp?: PointerEventHandler<HTMLButtonElement>
  /**
   * Click event handler to attach to the button.
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Mouse Down event handler to attach to the button.
   */
  onMouseDown?: MouseEventHandler<HTMLButtonElement>
  /**
   * Mouse Up handler to attach to the button.
   */
  onMouseUp?: MouseEventHandler<HTMLButtonElement>
  /**
   * Mouse Leave handler to attach to the button.
   */
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>
  /**
   * Touch Start handler to attach to the button.
   */
  onTouchStart?: TouchEventHandler<HTMLButtonElement>
  /**
   * Touch End handler to attach to the button.
   */
  onTouchEnd?: TouchEventHandler<HTMLButtonElement>
} & Omit<HTMLProps<HTMLDivElement>, 'onClick'>
